
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  fetchDashboardTotals,
  fetchDashboardOverview,
  fetchPaperAttendance,
  fetchStudentAttendanceOverview
} from "../redux/actions/dashboardActions";
import { 
  FiUsers, 
  FiUserCheck, 
  FiCalendar, 
  FiBook,
  FiTrendingUp,
  FiRefreshCw,
  FiStar,
  FiAward,
  FiClock,
  FiPercent,
  FiDownload
} from "react-icons/fi";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line
} from "recharts";

const Dashboard = () => {
  const dispatch = useDispatch();
  const {
    totals,
    overview,
    paperAttendance,
    studentOverview,
    loading,
    error
  } = useSelector((state) => state.dashboard);

  const [selectedClass, setSelectedClass] = useState("All Classes");
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch all dashboard data
  useEffect(() => {
    dispatch(fetchDashboardTotals());
    dispatch(fetchDashboardOverview());
    dispatch(fetchPaperAttendance());
    dispatch(fetchStudentAttendanceOverview(currentPage, 10));
  }, [dispatch, currentPage]);

  // Refresh data
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    dispatch(fetchDashboardTotals());
    dispatch(fetchDashboardOverview());
    dispatch(fetchPaperAttendance());
    dispatch(fetchStudentAttendanceOverview(currentPage, 10));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= studentOverview.pagination.totalPages) {
      setCurrentPage(newPage);
      dispatch(fetchStudentAttendanceOverview(newPage, 10));
    }
  };

  // CSV Export Function
  const exportToCSV = () => {
    if (studentOverview.data.length === 0) return;

    const headers = ['Student Name', 'Class Roll', 'Attendance Rate (%)', 'Today\'s Status', 'Last Updated'];
    
    const csvData = studentOverview.data.map(student => [
      student.student_name || '',
      student.class_roll || '',
      formatAttendanceRate(student.attendance_rate || 0),
      student.todays_status || 'Absent',
      student.last_updated ? new Date(student.last_updated).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      }) : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `student-attendance-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Process stats cards data
  const stats = {
    cards: [
      {
        id: 1,
        title: "Total Students",
        value: totals.totalStudents || overview.totalStudents || 0,
        growth: 5,
        icon: "users",
        description: "Enrolled in 2024-25",
        iconComponent: <FiUsers className="text-2xl" />
      },
      {
        id: 2,
        title: "Present Today",
        value: totals.presentToday || overview.presentToday || 0,
        growth: 3,
        icon: "user-check",
        description: "Across all classes",
        iconComponent: <FiUserCheck className="text-2xl" />
      },

      {
  id: 3,
  title: "Attendance Rate",
  value: (() => {
    const rate = totals.attendanceRate || overview.attendanceRate || 0;
    // If it's already a percentage (contains % or is > 1)
    if (rate.toString().includes('%') || parseFloat(rate) > 1) {
      return rate.toString().includes('%') ? rate : `${rate}%`;
    }
    // If it's a decimal (0-1)
    return `${(parseFloat(rate) * 100).toFixed(1)}%`;
  })(),
  growth: 1.2,
  icon: "calendar",
  description: "This week average",
  iconComponent: <FiPercent className="text-2xl" />
},
      {
        id: 4,
        title: "Perfect Attendance",
        value: totals.perfectAttendance || overview.perfectAttendance || 0,
        growth: 8,
        icon: "star",
        description: "This month",
        iconComponent: <FiStar className="text-2xl" />
      }
    ]
  };

  // Process weekly trend data
  const weeklyTrend = overview.weeklyTrend?.map(item => ({
    day: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' }),
    attendance: item.present > 0 ? ((item.present / totals.totalStudents) * 100).toFixed(1) : 0,
    present: item.present || 0
  })) || [
    { day: "Mon", attendance: 0, present: 0 },
    { day: "Tue", attendance: 0, present: 0 },
    { day: "Wed", attendance: 0, present: 0 },
    { day: "Thu", attendance: 0, present: 0 },
    { day: "Fri", attendance: 0, present: 0 },
    { day: "Sat", attendance: 0, present: 0 }
  ];

  // Process paper attendance data for chart - FIXED
  const paperStats = (() => {
    // Check if paperAttendance has data property (new API format)
    if (paperAttendance && paperAttendance.data && Array.isArray(paperAttendance.data)) {
      return paperAttendance.data.map((paper, index) => ({
        name: paper.paper_num || `Paper ${paper.paper_id}`,
        paperName: paper.paper_name || `Paper ${paper.paper_id}`,
        paperNum: paper.paper_num || `P${paper.paper_id}`,
        present: paper.total_present || 0,
        attendanceRate: parseFloat(paper.attendance_percentage || 0) * 100,
        totalStudents: paper.total_students || 0,
        semester: `Sem ${paper.sem_num || 0}`
      }));
    }
    // Check if paperAttendance is already an array (old format)
    else if (Array.isArray(paperAttendance)) {
      return paperAttendance.map((paper, index) => ({
        name: paper.paper_num || `Paper ${paper.paper_id}`,
        paperName: paper.paper_name || `Paper ${paper.paper_id}`,
        paperNum: paper.paper_num || `P${paper.paper_id}`,
        present: paper.total_present || 0,
        attendanceRate: parseFloat(paper.attendance_percentage || 0) * 100,
        totalStudents: paper.total_students || 0,
        semester: `Sem ${paper.sem_num || 0}`
      }));
    }
    // Return empty array if no data
    return [];
  })();

  // Calculate attendance counts for pie chart
  const presentCount = totals.presentToday || overview.presentToday || 0;
  const absentCount = (totals.totalStudents || overview.totalStudents || 0) - presentCount;
  
  const attendanceData = [
    { name: "Present", value: presentCount, color: "#10b981" },
    { name: "Absent", value: absentCount, color: "#ef4444" }
  ];

  const cardColors = [
    "from-purple-600 to-pink-600",
    "from-blue-600 to-cyan-600",
    "from-emerald-600 to-green-600",
    "from-amber-600 to-orange-600",
  ];

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-white">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for paper attendance chart
  const PaperTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const paperData = paperStats.find(p => p.paperNum === label);
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-[250px]">
          <p className="font-semibold text-gray-900 dark:text-white mb-1">{paperData?.paperName || label}</p>
          {paperData && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Paper: {paperData.paperNum}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Semester: {paperData.semester}</p>
            </>
          )}
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">{entry.name}:</span>
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {entry.name === 'Attendance Rate %' ? `${entry.value.toFixed(1)}%` : entry.value}
              </span>
            </div>
          ))}
          {paperData && (
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Students: {paperData.totalStudents}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Present Rate: {((paperData.present / paperData.totalStudents) * 100).toFixed(1)}%
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Format attendance rate for display
  const formatAttendanceRate = (rate) => {
    if (typeof rate === 'number') {
      return rate.toFixed(1);
    }
    if (typeof rate === 'string') {
      return (parseFloat(rate) * 100).toFixed(1);
    }
    return "0.0";
  };

  if (loading && refreshKey === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiUsers className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-xl transition-all hover:shadow-lg hover:shadow-purple-500/25"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div key={refreshKey} className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            GMGC Student Attendance Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gokhale Memorial Girls' College - Student Attendance Management
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 sm:mt-0">
          <div className="text-right hidden md:block">
            <p className="text-sm text-gray-500 dark:text-gray-400">Last Updated</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-purple-500/25 disabled:opacity-50"
          >
            <FiRefreshCw className={`text-lg ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* College Info Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 mb-8 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Gokhale Memorial Girls' College</h2>
            <p className="text-purple-100">ESTD 1938 • Kolkata • Student Attendance Management System</p>
          </div>
          <div className="mt-4 md:mt-0 text-right">
            <p className="text-3xl font-bold">{new Date().toLocaleDateString('en-IN', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
            <p className="text-purple-200">Academic Session 2024-2025</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.cards.map((card, idx) => (
          <div
            key={card.id}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 dark:border-gray-700 overflow-hidden group"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${cardColors[idx]} opacity-5 group-hover:opacity-10 transition-opacity`} />
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">
                    {card.title}
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {card.value.toLocaleString()}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {card.description}
                  </p>
                  <div className="flex items-center gap-1 mt-2">
                    {card.growth > 0 ? (
                      <FiTrendingUp className="text-green-500 text-sm" />
                    ) : (
                      <FiTrendingUp className="text-red-500 text-sm rotate-180" />
                    )}
                    <span className={`text-sm font-medium ${card.growth > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {card.growth > 0 ? '+' : ''}{card.growth}%
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">from last week</span>
                  </div>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${cardColors[idx]} text-white shadow-lg`}>
                  {card.iconComponent}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Today's Attendance Pie Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Today's Attendance Overview
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                {presentCount + absentCount} Students Recorded
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span>Present ({presentCount})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span>Absent ({absentCount})</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={attendanceData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  label={({ name, percent }) => 
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                  labelLine={false}
                >
                  {attendanceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Weekly Attendance Trend */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Weekly Attendance Trend
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                Daily attendance percentage for this week
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm font-medium">
                This Week
              </span>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyTrend}>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  className="stroke-gray-200 dark:stroke-gray-700"
                />
                <XAxis 
                  dataKey="day" 
                  tick={{ fontSize: 12 }}
                  className="text-gray-600 dark:text-gray-400"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  className="text-gray-600 dark:text-gray-400"
                  domain={[0, 100]}
                  label={{ 
                    value: 'Attendance %', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: -10,
                    style: { fill: '#6b7280' }
                  }}
                />
                <Tooltip 
                  content={<CustomTooltip />}
                  formatter={(value) => [`${value}%`, 'Attendance']}
                />
                <Line 
                  type="monotone" 
                  dataKey="attendance" 
                  stroke="#8b5cf6" 
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Attendance %"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Paper-wise Attendance Bar Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Paper-wise Attendance Analysis
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Attendance breakdown by paper/subject
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Present Students</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Attendance Rate</span>
              </div>
            </div>
          </div>
        </div>
        <div className="h-80">
          {paperStats.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={paperStats}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  className="stroke-gray-200 dark:stroke-gray-700"
                />
                <XAxis 
                  dataKey="paperNum" 
                  angle={0}
                  tick={{ fontSize: 12 }}
                  className="text-gray-600 dark:text-gray-400"
                  label={{ 
                    value: 'Paper Number', 
                    position: 'insideBottom',
                    offset: -50,
                    style: { fill: '#6b7280' }
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  className="text-gray-600 dark:text-gray-400"
                  yAxisId="left"
                  label={{ 
                    value: 'Present Students', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: -10,
                    style: { fill: '#6b7280' }
                  }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  className="text-gray-600 dark:text-gray-400"
                  domain={[0, 100]}
                  label={{ 
                    value: 'Attendance Rate %', 
                    angle: 90, 
                    position: 'insideRight',
                    offset: -10,
                    style: { fill: '#6b7280' }
                  }}
                />
                <Tooltip 
                  content={<PaperTooltip />}
                  cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                />
                <Legend 
                  wrapperStyle={{ 
                    paddingTop: '20px',
                    fontSize: '14px'
                  }}
                />
                <Bar 
                  yAxisId="left"
                  dataKey="present" 
                  name="Present Students" 
                  fill="#10b981" 
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  yAxisId="right"
                  dataKey="attendanceRate" 
                  name="Attendance Rate %" 
                  fill="#3b82f6" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <FiBook className="text-6xl text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Paper Data</h3>
                <p className="text-gray-600 dark:text-gray-400">No paper attendance data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Student Attendance Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Student Attendance Overview
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                Showing {studentOverview.data.length} of {studentOverview.pagination.totalRecords || 0} students
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={exportToCSV}
                disabled={studentOverview.data.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                  studentOverview.data.length === 0
                    ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:shadow-lg hover:shadow-green-500/25"
                }`}
              >
                <FiDownload className="text-lg" />
                Export CSV
              </button>
              <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-medium">
                Page {currentPage}
              </span>
            </div>
          </div>
        </div>
        
        {studentOverview.data.length === 0 ? (
          <div className="py-20 text-center">
            <FiUsers className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Student Data</h3>
            <p className="text-gray-600 dark:text-gray-400">No student attendance data available</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Class Roll
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Attendance Rate
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Today's Status
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {studentOverview.data.map((student, index) => (
                    <tr 
                      key={index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md">
                            {student.student_name?.charAt(0) || 'S'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {student.student_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {student.class_roll}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                (student.attendance_rate || 0) >= 75 ? 'bg-green-500' :
                                (student.attendance_rate || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(student.attendance_rate || 0, 100)}%` }}
                            ></div>
                          </div>
                          <span className={`text-sm font-medium ${
                            (student.attendance_rate || 0) >= 75 ? 'text-green-600 dark:text-green-400' :
                            (student.attendance_rate || 0) >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {formatAttendanceRate(student.attendance_rate || 0)}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                            student.todays_status === "Present"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                          }`}
                        >
                          {student.todays_status || "Absent"}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-600 dark:text-gray-400">
                        {student.last_updated ? new Date(student.last_updated).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        }) : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, studentOverview.pagination.totalRecords || 0)} of {studentOverview.pagination.totalRecords || 0} students
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                      currentPage === 1
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    <FiClock className="w-4 h-4" />
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, studentOverview.pagination.totalPages || 1) }, (_, i) => {
                      let pageNum;
                      if (studentOverview.pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= studentOverview.pagination.totalPages - 2) {
                        pageNum = studentOverview.pagination.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      if (pageNum < 1 || pageNum > studentOverview.pagination.totalPages) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg transition ${
                            currentPage === pageNum
                              ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
                              : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === studentOverview.pagination.totalPages}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${
                      currentPage === studentOverview.pagination.totalPages
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    Next
                    <FiClock className="w-4 h-4 rotate-180" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer Info */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <FiBook className="text-xl" />
            <h3 className="text-lg font-semibold">Attendance Policy</h3>
          </div>
          <p className="text-purple-100 text-sm">
            Minimum 75% attendance required. Medical leave requires documentation.
          </p>
        </div>
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <FiAward className="text-xl" />
            <h3 className="text-lg font-semibold">Top Performing Paper</h3>
          </div>
          <p className="text-blue-100 text-sm">
            {paperStats.length > 0 
              ? `${paperStats[0]?.paperName}: ${paperStats[0]?.attendanceRate?.toFixed(1)}% attendance`
              : "No paper data available"}
          </p>
        </div>
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <FiCalendar className="text-xl" />
            <h3 className="text-lg font-semibold">System Info</h3>
          </div>
          <p className="text-emerald-100 text-sm">
            GMGC Student Attendance System • Last sync: Just now • Total Students: {totals.totalStudents || overview.totalStudents || 0}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;